// src/components/CacheFreshnessBanner.tsx
// Polls /api/cache/freshness on the tenant's agent every 60 s and renders a
// small floating pill in the top-right showing the most recent sync time.
// Color shifts to amber when any cache table is >30 min stale and to red
// when >2 h. Hides itself silently on network errors -- the user shouldn't
// see a scary "agent unreachable" indicator just because a poll missed.

import { useEffect, useState } from 'react';
import { apiV2 } from '../services/apiV2';
import type { CacheFreshnessResponse, CacheFreshnessStatus } from '../types';

const POLL_INTERVAL_MS = 60_000;

export default function CacheFreshnessBanner() {
  const [data, setData] = useState<CacheFreshnessResponse | null>(null);
  const [hadError, setHadError] = useState(false);
  // Tick once a minute so the "X min ago" relative-time text refreshes even
  // when the underlying data hasn't changed.
  const [, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const r = await apiV2.getCacheFreshness();
        if (!cancelled) {
          setData(r);
          setHadError(false);
        }
      } catch {
        // Don't surface transient failures -- the agent might be restarting
        // or the tenant might not have an agent URL configured yet. We just
        // stop showing the banner until the next successful poll.
        if (!cancelled) setHadError(true);
      }
    };

    fetchOnce();
    const fetchTimer = setInterval(fetchOnce, POLL_INTERVAL_MS);
    const tickTimer = setInterval(() => setTick(t => t + 1), 60_000);

    return () => {
      cancelled = true;
      clearInterval(fetchTimer);
      clearInterval(tickTimer);
    };
  }, []);

  if (hadError || !data) return null;

  const label = formatLabel(data);
  const style = colorClasses(data.status);
  const title = buildTooltip(data);

  return (
    <div
      className={`fixed top-2 right-2 z-50 px-3 py-1 rounded-full text-xs font-medium shadow-md backdrop-blur-sm border ${style}`}
      title={title}
      role="status"
      aria-live="polite"
    >
      {label}
    </div>
  );
}

function colorClasses(status: CacheFreshnessStatus): string {
  switch (status) {
    case 'fresh':
      return 'bg-slate-800/80 text-slate-300 border-slate-700';
    case 'stale':
      return 'bg-amber-900/80 text-amber-200 border-amber-700';
    case 'down':
      return 'bg-red-900/80 text-red-200 border-red-700';
    case 'disabled':
      return 'bg-slate-800/60 text-slate-500 border-slate-700';
    case 'initializing':
    default:
      return 'bg-slate-800/60 text-slate-400 border-slate-700';
  }
}

function formatLabel(d: CacheFreshnessResponse): string {
  if (!d.enabled) return 'Cache off';
  if (d.status === 'initializing' || !d.newestSyncAt) return 'Loading data...';

  const newest = new Date(d.newestSyncAt);
  const clock = newest.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const rel = relativeTime(newest);
  return `Last sync: ${clock} (${rel})`;
}

function buildTooltip(d: CacheFreshnessResponse): string {
  if (!d.tables || d.tables.length === 0) return d.status;
  const lines = d.tables.map(t => {
    const at = t.lastSyncAt ? new Date(t.lastSyncAt).toLocaleString() : '—';
    const err = t.lastError ? ` (error: ${t.lastError.slice(0, 80)})` : '';
    return `${t.name}: ${t.rowCount.toLocaleString()} rows, last ${at}${err}`;
  });
  return lines.join('\n');
}

function relativeTime(d: Date): string {
  const elapsed = Date.now() - d.getTime();
  const sec = Math.round(elapsed / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} h ago`;
  const day = Math.round(hr / 24);
  return `${day} d ago`;
}
